import Axios from "axios";

import type { LanguageId } from "../util/languages";

export interface ChallengeStatus {
  challenge: Challenge;
  status: Status;
}

export interface Challenge {
  id: string;
  description: string;
  startTimeSecondsSinceEpoch?: number;
  endTimeSecondsSinceEpoch: number;
}

export interface ObjectiveProgress {
  progress: number;
  goal: number;
  isComplete: boolean;
}

export interface BaseStatus {
  userId: number;
  learningLanguage: LanguageId;
  fromLanguage: LanguageId;
  dueTimeSecondsSinceEpoch: number;
  xpObjective: ObjectiveProgress;
  androidXpObjective?: ObjectiveProgress;
  streakObjective?: ObjectiveProgress;
  hasCompletedSurvey: boolean;
  surveyLink?: string;
  email: string;
  isDuo: boolean;
}

export type Status = {
  counterpart?: BaseStatus;
} & BaseStatus;

export interface LeaderboardParticipant {
  rank: number;
  userId: number;
  learningLanguage: LanguageId;
  fromLanguage: LanguageId;
  duolingoEmailPrefix?: string;
  sponsorDuolingoEmailPrefix?: string;
  gainedXp: number;
}

export interface LeaderboardResponse {
  leaderboard: LeaderboardParticipant[];
  counterpart?: LeaderboardParticipant;
  self?: LeaderboardParticipant;
}

export interface SponseeCandidateResponse {
  userEmail?: string;
  errors: string[];
}

/**
 * Response for bulk user registration.
 * Provides detailed information about the registration operation including
 * counts and lists of users in different states.
 */
export interface BulkRegistrationResponse {
  /** Number of users successfully registered */
  successCount: number;
  /** Number of users who were already registered for this challenge */
  alreadyRegisteredCount: number;
  /** List of user IDs that were already registered */
  alreadyRegisteredUsers: number[];
  /** Total number of emails that failed (either not found or registration error) */
  notFoundCount?: number;
  /**
   * List of emails that could not be found in the system (no user ID exists for these emails).
   * These emails were provided but don't correspond to any user account.
   */
  emailsNotFound?: string[];
  /**
   * List of emails that failed during registration (user ID was found but registration failed).
   * These emails correspond to valid users, but something went wrong during the registration process.
   */
  emailsRegistrationFailed?: string[];
}

const client = Axios.create({
  baseURL: process.env.REACT_APP_LANGUAGE_CHALLENGE_API_ORIGIN,
});

const getChallenges = async () =>
  (
    await client.get<Challenge[]>("/challenges", {
      withCredentials: false,
    })
  ).data;

const getStatus = async () =>
  (
    await client.get<[ChallengeStatus]>("/status", {
      withCredentials: true,
    })
  ).data;

const getLeaderboard = async (challengeId: string) =>
  (
    await client.get<LeaderboardResponse>("/leaderboard", {
      params: {
        challengeId,
      },
      withCredentials: true,
    })
  ).data;

const getExternalLeaderboard = async (
  challengeId: string,
  options?: { limit?: number; includeAll?: boolean },
) =>
  (
    await client.get<LeaderboardResponse>("/external-leaderboard", {
      params: options?.includeAll
        ? { challengeId }
        : options?.limit !== undefined && options?.limit !== null
          ? { challengeId, limit: options.limit }
          : { challengeId, limit: 10 },
      withCredentials: true,
    })
  ).data;

const postDuoRegistration = async (
  duolingoEmailPrefix: string,
  learningLanguage: LanguageId,
  fromLanguage: LanguageId,
  challengeId: string,
) => {
  await client.post(
    "/duo-registration",
    {
      challengeId,
      duolingoEmailPrefix,
      fromLanguage,
      learningLanguage,
    },
    {
      withCredentials: true,
    },
  );
};

const postSponseeRegistration = async (
  sponseeUserId: number,
  learningLanguage: LanguageId,
  fromLanguage: LanguageId,
  email: string,
) => {
  await client.post(
    "/sponsee-registration",
    {
      email,
      fromLanguage,
      learningLanguage,
      userId: sponseeUserId,
    },
    {
      withCredentials: true,
    },
  );
};

const deleteRegistration = async (userId: number, challengeId: string) => {
  await client.delete("/registration", {
    data: {
      challengeId,
      userId,
    },
    withCredentials: true,
  });
};

export interface CreateChallengeRequest {
  challengeId: string;
  description: string;
  startTimestamp: string;
  endTimestamp: string;
  objectiveIds: string[];
}

const createChallenge = async (request: CreateChallengeRequest) => {
  await client.post("/create-challenge", request, {
    withCredentials: true,
  });
};
const postBulkUserRegistration = async (
  emails: string[],
  challengeId: string,
  learningLanguage: LanguageId,
  fromLanguage: LanguageId,
) =>
  (
    await client.post<BulkRegistrationResponse>(
      "/bulk-user-registration",
      {
        challengeId,
        emails,
        fromLanguage,
        learningLanguage,
      },
      {
        withCredentials: true,
      },
    )
  ).data;

export const languageChallengeApi = {
  createChallenge,
  deleteRegistration,
  getChallenges,
  getExternalLeaderboard,
  getLeaderboard,
  getStatuses: getStatus,
  postBulkUserRegistration,
  postDuoRegistration,
  postSponseeRegistration,
};
